import { Add, Delete } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import HtmlColorField from '../../components/HtmlColorField';
import Loading from '../../components/Loading';
import TimeInputWithBlur from '../../components/TimeInputWithBlur';
import { sortPrintersByName } from '../../helpers/printers';
import { useReloadOnPageRestore } from '../../hooks/useReloadOnPageRestore';
import {
    buildJobPayload,
    durationToMinutes,
    filamentsToColors,
    JOB_STATUSES,
    jobFromApi,
    numericInputProps,
    STATUSES_REQUIRING_REASON,
    STATUSES_SYNC_PRINTED_COUNT,
} from '../../helpers/jobs';

function EditJobPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [printers, setPrinters] = useState([]);
    const [form, setForm] = useState({
        printer_uuid: '',
        file_name: '',
        estimated_duration: '',
        estimated_filament_consumption_grams: '',
        started_at: '',
        finished_at: '',
        status: 'sucesso',
        status_reason: '',
        object_count: '1',
        printed_count: '0',
    });
    const [filamentColors, setFilamentColors] = useState(['']);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [hasResponse, setHasResponse] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });
    const [reloadKey, setReloadKey] = useState(0);

    const bumpReload = useCallback(() => {
        setReloadKey((k) => k + 1);
    }, []);

    useReloadOnPageRestore(bumpReload);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            api.get('printers?page=1&limit=100'),
            api.get(`jobs/${id}`),
        ])
            .then(([printersRes, jobRes]) => {
                setPrinters(sortPrintersByName(printersRes.data.printers || []));
                const data = jobRes.data.data || {};
                const parsed = jobFromApi(data);
                const objectCount = String(parsed.object_count);
                setForm({
                    ...parsed,
                    estimated_filament_consumption_grams: String(parsed.estimated_filament_consumption_grams),
                    object_count: objectCount,
                    printed_count: parsed.status === 'sucesso' ? objectCount : String(parsed.printed_count),
                });
                setFilamentColors(filamentsToColors(data.filaments));
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id, reloadKey]);

    const updateForm = (field, value) => {
        setForm((prev) => {
            const next = { ...prev, [field]: value };

            if (field === 'status' && (value === 'sucesso' || STATUSES_SYNC_PRINTED_COUNT.includes(value))) {
                next.printed_count = String(next.object_count);
            }

            if (field === 'object_count' && (prev.status === 'sucesso' || STATUSES_SYNC_PRINTED_COUNT.includes(prev.status))) {
                next.printed_count = String(value);
            }

            return next;
        });
    };

    const handleFilamentChange = (index, value) => {
        setFilamentColors((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const addFilamentRow = () => {
        setFilamentColors((prev) => [...prev, '']);
    };

    const removeFilamentRow = (index) => {
        setFilamentColors((prev) => {
            if (prev.length <= 1) {
                return [''];
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false;
        const nextErrors = { has_errors: false };

        if (!form.printer_uuid) {
            hasError = true;
            nextErrors.printer_uuid = 'Selecione a impressora utilizada nesta impressão.';
        }
        if (!form.file_name.trim()) {
            hasError = true;
            nextErrors.file_name = 'O nome do arquivo é obrigatório.';
        }
        if (STATUSES_REQUIRING_REASON.includes(form.status) && !form.status_reason.trim()) {
            hasError = true;
            nextErrors.status_reason = 'Informe o motivo para este status.';
        }
        if (form.estimated_duration.replace(/\D/g, '') && durationToMinutes(form.estimated_duration) === null) {
            hasError = true;
            nextErrors.estimated_duration = 'Minutos inválidos (use até 59).';
        }

        if (hasError) {
            nextErrors.has_errors = true;
            setResultErrors(nextErrors);
            return;
        }

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        api.put(`jobs/${id}`, buildJobPayload(form, filamentColors))
            .then(() => {
                setHasResponse(true);
                navigate('/admin/jobs');
            })
            .catch((error) => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (e) {
                    console.log(e);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/jobs')} to={'/admin/jobs'}>
                    Impressões
                </Link>
                <Typography color="text.primary">Editando impressão</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Impressão</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="printer-label">Impressora</InputLabel>
                            <Select
                                labelId="printer-label"
                                label="Impressora"
                                value={form.printer_uuid}
                                onChange={(e) => updateForm('printer_uuid', e.target.value)}
                            >
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('printer_uuid')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome do arquivo"
                            fullWidth
                            value={form.file_name}
                            onChange={(e) => updateForm('file_name', e.target.value)}
                        />
                        {renderFieldError('file_name')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TimeInputWithBlur
                            label="Duração Estimada"
                            value={form.estimated_duration}
                            onChange={(e) => updateForm('estimated_duration', e.target.value)}
                        />
                        {renderFieldError('estimated_duration')}
                        {renderFieldError('estimated_duration_minutes')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Filamento estimado (g)"
                            fullWidth
                            type="number"
                            inputProps={numericInputProps(0)}
                            value={form.estimated_filament_consumption_grams}
                            onChange={(e) => updateForm('estimated_filament_consumption_grams', e.target.value)}
                        />
                        {renderFieldError('estimated_filament_consumption_grams')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Início"
                            fullWidth
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            inputProps={numericInputProps()}
                            value={form.started_at}
                            onChange={(e) => updateForm('started_at', e.target.value)}
                        />
                        {renderFieldError('started_at')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Fim"
                            fullWidth
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            inputProps={numericInputProps()}
                            value={form.finished_at}
                            onChange={(e) => updateForm('finished_at', e.target.value)}
                        />
                        {renderFieldError('finished_at')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                label="Status"
                                value={form.status}
                                onChange={(e) => updateForm('status', e.target.value)}
                            >
                                {JOB_STATUSES.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('status')}
                    </Grid>
                    {STATUSES_REQUIRING_REASON.includes(form.status) && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Motivo do status"
                                fullWidth
                                multiline
                                minRows={2}
                                value={form.status_reason}
                                onChange={(e) => updateForm('status_reason', e.target.value)}
                            />
                            {renderFieldError('status_reason')}
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Quantidade de objetos"
                            fullWidth
                            type="number"
                            inputProps={numericInputProps(1)}
                            value={form.object_count}
                            onChange={(e) => updateForm('object_count', e.target.value)}
                        />
                        {renderFieldError('object_count')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Objetos impressos"
                            fullWidth
                            type="number"
                            inputProps={numericInputProps(0)}
                            value={form.printed_count}
                            onChange={(e) => updateForm('printed_count', e.target.value)}
                            disabled={form.status === 'sucesso'}
                        />
                        {renderFieldError('printed_count')}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Filamentos (multicor)
                        </Typography>
                        {filamentColors.map((color, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                                <HtmlColorField
                                    label={`Cor ${index + 1}`}
                                    value={color}
                                    preserveLiteral
                                    onChange={(nextColor) => handleFilamentChange(index, nextColor)}
                                />
                                <IconButton
                                    color="error"
                                    onClick={() => removeFilamentRow(index)}
                                    aria-label="remover cor"
                                    sx={{ mt: 1 }}
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}
                        <Button startIcon={<Add />} onClick={addFilamentRow} type="button">
                            Adicionar cor
                        </Button>
                        {renderFieldError('filaments')}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Atualizando...' : 'Atualizar Impressão'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default EditJobPage;
